import { lazyLoader, lazyLoaderBg, lazyLoaderSvg, lazyLoaderLottie } from "./helpers/lazy-loader";
import { isInViewport } from "./is-in-viewport";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import Menu from './components/menu';

[lazyLoader, lazyLoaderBg, lazyLoaderSvg, lazyLoaderLottie].forEach(fn => fn());

// Register images
import.meta.glob('../images/**/*.{jpg,jpeg,png,webp,svg}', { query: '?url', import: 'default' });
import.meta.glob('../img/**/*.{jpg,jpeg,png,webp,svg,webmanifest}', { query: '?url', import: 'default' });

gsap.registerPlugin(ScrollTrigger);

const res = {
  isTablet: window.innerWidth < 1024,
  isMobile: window.innerWidth <= 768,
  isSmallMobile: window.innerWidth <= 359
};

const curPage = document.body.getAttribute("id");

const getUTMParams = () => {
  const queryString = window.location.search.split('?')[1];
  const params = {};

  if (queryString && queryString.length > 1) {
    const pairs = queryString.split('&');

    pairs.forEach(pair => {
      const keyValue = pair.split('=');
      const key = keyValue[0];
      if (key.includes('utm_')) {
        const value = keyValue[1];
        params[key] = value;
      }
    });

    sessionStorage.setItem('utm_data', JSON.stringify(params));
  }
}

getUTMParams();

let menuInstance = new Menu();

const debounce = (fn, wait = 200) => {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

const onResize = debounce(() => {
  menuInstance.destroy();
  menuInstance = new Menu();
}, 250);

window.addEventListener('resize', onResize);

// Reveal script
[...document.querySelectorAll('section')].forEach(section => {
  let lastTriggeredTime = 0;
  [...section.querySelectorAll('[data-reveal]')].forEach(item => {    
    ScrollTrigger.create({
      trigger: item,
      start: 'top 95%',
      onEnter: () => {
        const now = performance.now();
        const delay = Math.max(0, 100 - (now - lastTriggeredTime));
        lastTriggeredTime = now + delay;

        gsap.to(item, {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          ease: 'power2.out',
          delay: delay / 1000
        });
      },
      toggleActions: 'play none none none',
      once: true
    });
  })
});

ScrollTrigger.addEventListener("refreshInit", () => gsap.set("[data-reveal]", {y: 0}));

if (curPage == "procurement-software-page") {
  isInViewport(document.getElementById("scalability"), async () => {
    const module = await import("./pages/index");
    module.initLootieCheckboxes();
  });
}

if (curPage == 'customer-cases') {
  isInViewport(document.getElementById("case-studies-section"), async () => {
    const CaseStudiesFilter = (await import("./pages/customer-cases")).default;
    new CaseStudiesFilter(document.getElementById("case-studies-section"));
  });

  isInViewport(document.getElementById("roi-section"), async () => {
    (await import("./pages/customer-cases")).roiSliderInit(document.getElementById("roi-section"));
  });
}

if (curPage !== 'small-midsize-business' && curPage !== 'customer-cases' && curPage !== 'hospitality' && curPage !== 'non-profit') {
  isInViewport(document.querySelector(".js-partners-section"), async () => {
    const module = await import("./main");
    module.initPartnersSlider();
  });
} 

if (curPage == 'accounts-payable-roi') {
  if (document.location.hash.includes('ap-roi-calculator')) {
    import('./pages/ap-roi').then(module => module.scrollToCalculator());
  }
}

if (curPage == 'about') {
  isInViewport(document.querySelector(".galleryCards"), async () => {
    const module = await import("./pages/about");
    module.galleryCards();
    module.copyEmail();
  });

  isInViewport(document.getElementById("numbers-section"), async () => {
    const module = await import("./components/animation");
    module.initNumberAnimations(document.getElementById("numbers-section"));
  });
}

if (curPage == 'guides-and-ebooks') {
  isInViewport(document.querySelector(".js-guides-section"), async () => {
    const GuidesEbooksFilter = (await import("./pages/resources/guides-and-ebooks")).default;
    new GuidesEbooksFilter();
  });

  isInViewport(document.querySelector(".js-hs-guides-ebooks-form-container"), async () => {
    const script = document.createElement('script');
    script.setAttribute('charset','utf-8');
    script.setAttribute('type','text/javascript');
    script.setAttribute('src','//js.hsforms.net/forms/embed/v2.js');

    document.body.appendChild(script);

    script.addEventListener('load', e => {
      hbspt.forms.create({
        target: "#guides-ebooks-form",
        region: "na1",
        portalId: "1903299",
        formId: "949737f3-84e4-4b91-8694-f830cba13fe8",
        cssClass: 'tw-hubspot-custom-form',
        onFormSubmitted: () => {
          const formContainer = document.querySelector('.js-hs-guides-ebooks-form-container');
          const messageContainer = document.createElement('div');
          formContainer.innerHTML = '';
          messageContainer.innerHTML = `
            <div class="w-full rounded-lg bg-night-blue-800 flex flex-col items-center p-[48px_32px] gap-8 max-md:gap-6 max-md:p-[32px_20px]">
              <img class="w-35 h-[138px] max-md:w-30 max-md:h-[118px]" width="140" height="138" src="${guideEbooksTranslations.imageUrl}" alt="Thank you for subscribe image" />
              <div class="flex flex-col items-center gap-3 max-md:gap-2">
                <h4 class="font-inter! text-22! font-semibold! text-center text-white! mb-0! max-lg:text-xl! max-md:text-lg!">${guideEbooksTranslations.thanksMessage.h4}</h4>
                <p class="tw-text-lg mb-0 text-center text-azure-light-400">${guideEbooksTranslations.thanksMessage.p}</p>
              </div>
            </div>`;
          formContainer.appendChild(messageContainer);
        }
      });
    })
  });
}

if (curPage == 'zip') {
  isInViewport(document.querySelector(".js-features-section"), async () => {
    const module = await import("./pages/zip");
    module.featuresSliderInit();
  });
}

if (curPage == 'why-precoro') {
  const whyPrecoroModule = import("./pages/why-precoro");
  const animationModule = import("./components/animation");
  
  isInViewport(document.querySelector(".js-tooltip"), () => {
    whyPrecoroModule.then(module => {
      module.initInfoTooltipEvent(document.querySelector(".js-tooltip"));
    })
  }, '0px 0px -200px 0px');

  isInViewport(document.querySelector("#roi-section"), () => {
    animationModule.then(module => {
      module.initNumberAnimations(document.querySelector("#roi-section"));
    });
  }, '0px 0px -200px 0px');
  
  isInViewport(document.querySelector("#benefits-section"), () => {
    whyPrecoroModule.then(module => {
      module.initBenefitsTabsSlider(document.querySelector("#benefits-section"));
    });
  });

  isInViewport(document.querySelector("#social-proof-section"), () => {
    animationModule.then(module => {
      module.initNumberAnimations(document.querySelector("#social-proof-section"));
    });
  });
}

if (curPage == 'purchasing-software') {
  if (!res.isTablet) {
    isInViewport(document.getElementById("cycle-section"), async () => {
      const module = await import("./pages/seo/purchasing-software");
      module.initCycleAnimation(document.getElementById("cycle-section"));
    });
  }
  isInViewport(document.getElementById("toggle-cards"), async () => {
    const module = await import("./pages/seo/purchasing-software");
    module.initCardsToggle(document.getElementById("toggle-cards"));
  });
}

isInViewport(document.getElementById("slider-section"), async () => {
  const module = await import("./pages/csrd-reporting");
  module.initSlider(document.getElementById("slider-section"));
});

if (curPage == 'product-page') {
  [...document.querySelectorAll(".js-product-tabs")].forEach((container) => {
    isInViewport(container, async () => {
      const tabsSlider = await import("./pages/product");
      await tabsSlider.init(container);
    }, '0px 0px 0px 0px');
  });
}

if (curPage == 'procurement') {
  const procurementModule = import("./pages/procurement");

  isInViewport(document.getElementById("product-section"), () => {
    procurementModule.then(module => {
      module.productSliderInit(document.getElementById("product-section"));
    });
  });
  isInViewport(document.getElementById("compare-section"), () => {
    procurementModule.then(module => {
      module.compareSliderInit(document.getElementById("compare-section"));
    });
  });
}

isInViewport(document.getElementById("integration-demo"), async () => {
  const module = await import("./components/modal-video");
  module.init(document.getElementById("integration-demo"));
});

if (curPage == 'customers') {
  import("./pages/customers").then(module => module.switchText());
}

if (curPage == 'cms-page') {
  [...document.querySelectorAll('.js-product-section')].forEach(section => {
    isInViewport(section, async () => {
      const productSlider = await import("./components/product-slider");
      productSlider.init(section);
    });
  });

  document.querySelectorAll('.js-core-value-section').forEach(section => {
    isInViewport(section, async () => {
      const coreValue = await import("./components/core-value");
      coreValue.init(section);
    });
  });

  document.querySelectorAll('[data-section="faq"]').forEach(section => {
    isInViewport(section, async () => {
      const coreValue = await import("./components/core-value");
      coreValue.init(section);
    });
  });

  document.querySelectorAll("[data-width='animated']").forEach(el => {
    isInViewport(el, async () => {
      const module = (await import('./components/sections.text-and-image-single-animated-width'));
      module.init(el);
    });
  });

  [...document.querySelectorAll('.js-stages-section')].forEach(section => {
    isInViewport(section, async () => {
      const stages = await import("./components/stages");
      stages.init(section);
    });
  });

  [...document.querySelectorAll('.js-advanced-product-slider')].forEach(section => {
    isInViewport(section, async () => {
      const advancedProductSlider = await import("./components/advanced-product-slider");
      advancedProductSlider.init(section);
    });
  });
  
  [...document.querySelectorAll('.js-3-in-1-section')].forEach(section => {
    isInViewport(section, async () => {
      const threeInOne = await import("./components/3-in-1");
      threeInOne.init(section);
    });
  });

  [...document.querySelectorAll('.js-compare-slider')].forEach(section => {
    isInViewport(section, async () => {
      const compareSlider = await import("./components/compare-slider");
      compareSlider.init(section);
    });
  });

  [...document.querySelectorAll('.js-customers-table')].forEach(container => {
    isInViewport(container, async () => {
      const customersTable = await import("./components/customers-table");
      customersTable.init(container);
    });
  });

  [...document.querySelectorAll('.js-step-by-step-section')].forEach(section => {
    if (!res.isTablet) {
      isInViewport(section, async () => {
        const stepByStep = await import("./components/step-by-step");
        stepByStep.init(section);
      });
    }
  });

  document.querySelector('.js-hero-section') &&
    import('./components/hero-video').then(module => module.init());
  
  isInViewport(document.getElementById("on-demand-webinars"), async () => {
    const onDemandSlider = await import("./components/on-demand-slider");
    onDemandSlider.init(document.getElementById("on-demand-webinars"));
  });
  
  isInViewport(document.getElementById("compare-nominations"), async () => {
    const compareNominations = await import("./components/compare-nominations");
    compareNominations.init(document.getElementById("compare-nominations"));
  });

  [...document.querySelectorAll('.js-integrations-punchouts-cards')].forEach(section => {
    if (res.isMobile) {
      isInViewport(section, async () => {
        const integrationsPunchoutsCards = await import("./components/section.integrations-punchouts-cards");
        integrationsPunchoutsCards.init(section);
      });
    }
  });

  [...document.querySelectorAll('.js-case-studies-reviews')].forEach(section => {
    isInViewport(section, async () => {
      const caseStudiesReivews = await import("./components/case-studies-reviews");
      caseStudiesReivews.init(section);
    });
  });
}

if (document.body.matches('.wall-of-love')) {
  (async () => {
    const module = await import('./pages/wall-of-love');

    module.tooltip();
    module.parallax();
    module.popupVideo();
    module.videoSection();
    module.slideUpCards();
  })();
}

if (res.isTablet) {
  isInViewport(document.querySelector(".sliderGallery"), async () => {
    const module = await import("./main");
    module.gallerySlider();
  });
}

[...document.querySelectorAll(".js-numbers-section")].forEach(section => {
  isInViewport(section, async () => {
    const module = await import("./components/animation");
    module.initNumberAnimations(section);
  });
});

[...document.querySelectorAll(".js-circle-animation")].forEach(element => {
  isInViewport(element, async () => {
    const module = await import("./components/animation");
    module.initCircleAnimation(element);
  });
});

[...document.querySelectorAll("#tabs-expand-card")].forEach(section => {
  isInViewport(section, async () => {
    const module = await import("./components/tabs-card-expand");
    module.init(section);
  });
});

[...document.querySelectorAll(".js-slider-logos")].forEach(container => {
  isInViewport(container, async () => {
    const sliderLogos = await import("./components/slider-logos");
    sliderLogos.init(container);
  }, '0px 0px -100px 0px');
});

[...document.querySelectorAll(".sliderTabs")].forEach((sliderTab) => {
  isInViewport(sliderTab, async () => {
    
    const module = await import("./main");
    await module.sliderTabs(sliderTab);
    if (curPage == "procurement-software-page") {
      const indexModule = await import("./pages/index");
      indexModule.checkWidthOfTabsSlider(sliderTab);
    }
  }, curPage == 'customer-cases' ? '0px 0px -200px 0px' : '');
});

[...document.querySelectorAll(".js-tabs-slider-container")].forEach((container) => {
  isInViewport(container, async () => {
    const tabsSlider = await import("./components/tabs-slider");
    await tabsSlider.init(container);
  }, '0px 0px -200px 0px');
});

[...document.querySelectorAll("svg.js-inline-svg")].forEach(async svgElement => {
  if (svgElement.getAttribute('lazy') == 'true') {
    isInViewport(svgElement, async () => {
      const inlineSvg = await import("./components/inline-svg");
      inlineSvg.init(svgElement);
    });
  } else {
    const inlineSvg = await import("./components/inline-svg");
    inlineSvg.init(svgElement);
  }
  
});

[...document.querySelectorAll('.js-customers-reviews')].forEach(section => {
  isInViewport(section, async () => {
    const customersReviewsSlider = await import("./components/customers-reviews");
    customersReviewsSlider.init(section);
  });
});

isInViewport(document.querySelector("[data-tab]"), async () => {
  const module = await import("./main");
  module.initTabs();
});

isInViewport(document.getElementById("customers-slider"), async () => {
  const module = await import("./main");
  module.initCustomers();
});

isInViewport(document.querySelector(".reviews-slider"), async () => {
  const module = await import("./main");
  module.initReviews();
});

isInViewport(document.querySelector(".sliderLogos"), async () => {
  const module = await import("./main");
  module.sliderLogos(curPage);
}, '0px 0px -100px 0px');

isInViewport(document.querySelector(".sliderJourney"), async () => {
  const module = await import("./main");
  module.sliderJourney();
});

isInViewport(document.querySelector(".caseStudySlider"), async () => {
  const module = await import("./main");
  module.caseStudySlider();
});

isInViewport(document.getElementById("case-studies-section"), async () => {
  const caseStudiesSlider = await import("./components/case-studies-slider");
  caseStudiesSlider.init(document.getElementById("case-studies-section"));
});

isInViewport(document.querySelector(".js-accordion"), async () => {
  const module = await import("./components/accordion");
  module.accordion();
});

isInViewport(document.querySelector(".js-coupa-tab-section"), async () => {
  const module = await import("./main");
  module.coupaTabsSwitch(document.querySelector(".js-coupa-tab-section"));
});

isInViewport(document.querySelector(".js-footer"), async () => {
  const module = await import("./components/footer");
  if (res.isSmallMobile) {
    module.mobileDropdownInit();
  }
});

isInViewport(document.querySelector(".lottieGrid"), async () => {
  const module = await import("./main");
  module.lottieHover();

  document.addEventListener('scroll', e => {
    module.lottieMobile();
  })
});

if (res.isMobile) {
  [...document.querySelectorAll(".js-tabs-buttons-slick")].forEach(element => {
    isInViewport(element, async () => {
      const module = await import("./main");
      module.tabsButtonsCarousel(element);
    });
  });
}

[...document.querySelectorAll(".js-tab-container")].forEach(element => {
  isInViewport(element, async () => {
    const module = await import("./components/tabs");
    module.tabs();
  });
})

isInViewport(document.querySelector(".js-screens-carousel"), async () => {
  const module = await import("./components/screens-carousel");
  module.default();
});

isInViewport(document.getElementById("workflow-section"), async () => {
  const workflowScripts = await import("./components/workflow");
  workflowScripts.initTabs(document.getElementById("workflow-section"));
});

[...document.querySelectorAll('.js-multistep-form')].forEach(form => {
  form.classList.remove('hidden');
  
  isInViewport(form, async () => {
    new (await import("./components/multistep-form")).default(form);
  });
});

isInViewport(document.querySelector('.js-csrd-reporting-scroll'), async () => {
  if (res.isTablet) return
  const module = await import("./vendors/scrollMagic");
  module.csrdReportingScroll(document.querySelector('.js-csrd-reporting-scroll'));
});

isInViewport(document.querySelector('.js-form'), async () => {
  const formRequest = (await import('./form_request')).default;
  new formRequest(document.querySelector('.js-form'));
});

document.querySelectorAll(".js-image-with-text-carousel-section").forEach(section => {
  isInViewport(section, async () => {
    const Swiper = (await import('swiper')).Swiper;
    const Navigation = (await import('swiper/modules')).Navigation;
    const Mousewheel = (await import('swiper/modules')).Mousewheel;
    const FreeMode = (await import('swiper/modules')).FreeMode;
  
    new Swiper(section.querySelector('.swiper'), {
      modules: [Navigation, Mousewheel, FreeMode],
      spaceBetween: 16,
      slidesPerView: 'auto',
      speed: 300,
      threshold: 5,
      freeMode: {
        enabled: true,
        sticky: true,
        momentum: true, 
        momentumRatio: 0.7,
        momentumVelocityRatio: 1,
      },
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
        thresholdDelta: 10,
      },
      navigation: {
        nextEl: section.querySelector('.js-next'),
        prevEl: section.querySelector('.js-prev')
      },
      on: {
        init: () => {
          document.body.classList.add('lg:overscroll-none');
        }
      },
    });
  });
})

document.querySelectorAll(".js-flip-cards-section").forEach(section => {
  if (window.innerWidth >= 768) return;

  isInViewport(section, async () => {
    const Swiper = (await import('swiper')).Swiper;
    const Navigation = (await import('swiper/modules')).Navigation;
    const Mousewheel = (await import('swiper/modules')).Mousewheel;
  
    new Swiper(section.querySelector('.swiper'), {
      modules: [Navigation, Mousewheel],
      spaceBetween: 16,
      slidesPerView: 'auto',
      speed: 1000,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
        thresholdDelta: 10,
      },
      navigation: {
        nextEl: section.querySelector('.js-next'),
        prevEl: section.querySelector('.js-prev')
      },
      on: {
        init: () => {
          document.body.classList.add('lg:overscroll-none');
        },
      },
    });
  });
})

let once = false;

const events = [
  'click',
  'scroll'
]

events.forEach(event => {
  window.addEventListener(event, async e => {
    if (once) return;
    once = true;

    gsap.registerPlugin(ScrollToPlugin);

    const links = document.querySelectorAll('a[href^="#"]');

    links.forEach(link => {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        const href = this.getAttribute('href');
        if (href === '#' || !href.trim()) return;
        
        const targetElement = document.querySelector(this.getAttribute('href'));
        
        if (targetElement) {
          gsap.to(window, {
            scrollTo: targetElement,
            duration: 2,
            ease: 'power2.out'
          });
        }
      });
    });

    if (curPage == "privacy-notice" || curPage == 'security' || curPage == 'terms') {
      const module = await import("./main");

      window.addEventListener("scroll", (e) => {
        module.fixedSidebar();
      });
    }

    if (curPage == 'product-page' || curPage == 'cfo-page') {
      const module = await import("./components/float-images");
      module.floatImages();
    }

    if (curPage == 'customer-cases' || curPage == 'small-midsize-business' || curPage == 'hospitality' || curPage == 'non-profit') {
      const module = await import("./main");
      module.initPartnersSlider();
    }

    if (curPage == 'accounts-payable-roi') {
      const module = await import("./pages/ap-roi");
      module.calculatorControl();
    }

    if (curPage == 'contact-us') {
      const hsChat = (await import("./components/hs-chat")).default;
      hsChat(event);
    }
  })
})